'use client';

import { useRouter } from 'next/navigation';
import { ComponentProps, FC, useCallback, useMemo } from 'react';
import type { ToastContentProps } from 'react-toastify';
import { shallow } from 'zustand/shallow';

import { handleCarmaClientErrorMessage } from 'src/data/Carma/helpers/handleCarmaClientErrorMessage';
import { SavedSearch } from 'src/data/SavedSearchApi/SavedSearchApi';
import { Button } from 'src/general/components/Button/Button';
import { ToasterService } from 'src/general/components/Toaster/ToasterService';
import { getOptimisticSavedSearches } from 'src/general/helpers/getOptimisticSavedSearches';
import {
  filtersPayloadRowToPlpStoreFilters,
  filtersPayloadRowToPlpStoreSimilarTo,
} from 'src/plp/helpers/filtersPayloadRowToPlpStoreFilters';
import { getPlpUrlFromStoreFilters } from 'src/plp/helpers/getPlpUrlFromStoreFilters';
import { authStore } from 'src/stores/authStore';
import { savedSearchStore } from 'src/stores/savedSearchStore';
import { FilterListPayload, PlpFilterQueryParams } from 'src/types/CarFilters.types';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { SearchAlertToggle } from './SearchAlertToggle';
import { handleSavedSearchApiUpdatesViaProxy } from './utils';

interface Props {
  filtersList?: FilterListPayload;
  filters?: PlpFilterQueryParams;
  isSimilarTo?: string;
  placement: string;
  Component: FC<ComponentProps<typeof SearchAlertToggle>>;
  targetSlug: string;
  savedSearchTypesenseQuery?: {
    q: string;
    filter_by: string;
    page: number;
  };
}

export const SearchAlertToggleProvider: FC<Props> = ({
  filtersList,
  filters,
  isSimilarTo,
  placement,
  Component,
  targetSlug,
  savedSearchTypesenseQuery,
}) => {
  const router = useRouter();
  const { isUserLoggedIn } = authStore((state) => ({ isUserLoggedIn: state.isUserLoggedIn }), shallow);
  const { savedSearches, savedSearchesInitialised, mappedSaveSearch, setSavedSearchesData } = savedSearchStore(
    (state) => ({
      savedSearches: state.savedSearches,
      savedSearchesInitialised: state.savedSearchesInitialised,
      mappedSaveSearch: state.mappedSaveSearch,
      setSavedSearchesData: state.setSavedSearchesData,
    }),
    shallow,
  );

  const {
    isSavedSearchActive,
    savedSearch,
  }: {
    isSavedSearchActive: boolean;
    savedSearch: SavedSearch | null;
  } = useMemo(() => {
    if (isSimilarTo && savedSearches) {
      const savedSearch = filtersPayloadRowToPlpStoreSimilarTo(savedSearches, isSimilarTo) ?? null;
      const isSavedSearchActive = !!savedSearch?.subscribed_at;
      return {
        savedSearch,
        isSavedSearchActive,
      };
    }

    if (filters && filtersList) {
      const mapKey = getPlpUrlFromStoreFilters(filtersPayloadRowToPlpStoreFilters(filtersList, filters.filters), {});
      const savedSearch = mappedSaveSearch.get(mapKey) ?? null;
      const isSavedSearchActive = !!savedSearch?.subscribed_at;
      return {
        isSavedSearchActive,
        savedSearch,
      };
    }

    return {
      isSavedSearchActive: false,
      savedSearch: null,
    };
  }, [filters, filtersList, isSimilarTo, mappedSaveSearch, savedSearches, savedSearchTypesenseQuery]);

  const snackbarAction = useCallback(
    ({ closeToast }: Pick<ToastContentProps, 'closeToast'>) => (
      <Button href="/account/notifications" variant="tertiary" size="small" onClick={closeToast}>
        Manage
      </Button>
    ),
    [],
  );

  const handleSavedSearchSwitch = useCallback(
    async (isActive: boolean) => {
      if (isUserLoggedIn) {
        const optimisticData = getOptimisticSavedSearches(
          savedSearches ?? [],
          savedSearch ?? null,
          isActive,
          filters ?? [],
        );
        setSavedSearchesData(optimisticData);
        try {
          const result = await handleSavedSearchApiUpdatesViaProxy(targetSlug, {
            savedSearch,
            filters: filters?.filters ?? [],
            isSimilarTo: isSimilarTo ?? null,
            isActive,
            typesenseQuery: savedSearchTypesenseQuery ? JSON.stringify(savedSearchTypesenseQuery) : '',
          });
          setSavedSearchesData(optimisticData?.map((search) => (search.id === 'tmp-id' ? result.data : search)) ?? []);
          pushToDataLayer({
            event: 'toggle_save_search',
            active: isActive,
            placement,
          });
          if (isActive) {
            ToasterService.success({
              message: 'Your search alert is active',
              action: { type: 'custom', actionComponent: snackbarAction },
            });
          }
        } catch (error) {
          const errorMessage = handleCarmaClientErrorMessage(error);
          pushToDataLayer({
            event: 'toggle_save_search-error',
            active: isActive,
            placement,
            message: errorMessage,
          });
          ToasterService.error({ message: errorMessage });
        }
      } else {
        // Set the pending search filters
        pushToDataLayer({
          event: 'toggle_save_search-attempt',
          active: true,
          placement,
        });
        // Redirect to login with a serialised pending search payload.
        const redirectUrl = new URL(window.location.toString());
        redirectUrl.searchParams.set(
          'savedSearch',
          JSON.stringify({
            filters: filters?.filters ?? [],
            plpSlug: targetSlug,
            typesenseQuery: savedSearchTypesenseQuery ? JSON.stringify(savedSearchTypesenseQuery) : '',
          }),
        );
        const queryParams = new URLSearchParams({
          type: 'carAlerts',
          redirect: redirectUrl.toString(),
          origin: redirectUrl.toString(),
        });
        router.push(`/forms/auth/start?${queryParams.toString()}`);
      }
    },
    [
      isUserLoggedIn,
      savedSearches,
      savedSearch,
      setSavedSearchesData,
      filters,
      isSimilarTo,
      targetSlug,
      placement,
      snackbarAction,
      router,
      savedSearchTypesenseQuery,
    ],
  );

  const componentProps: ComponentProps<typeof SearchAlertToggle> = {
    isActive: isSavedSearchActive,
    initialised: savedSearchesInitialised,
    onHandleSwitch: handleSavedSearchSwitch,
  };

  return <Component {...componentProps} />;
};
